import { ReactComponent as OutlineCalendar } from "assets/svg/outline-calendar.svg";
import { ReactComponent as Previous } from "assets/svg/outline-chevron-left-16.svg";
import { ReactComponent as Next } from "assets/svg/outline-chevron-right-16.svg";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import IconButton from "components/icon-button";
import {
  getStillAvailableSlotsForDay,
  today,
} from "components/orchid-pro/Utils";
import * as time_utils from "components/utility/time_utils";
import React, { useCallback, useMemo, useState } from "react";
import { Button, Col, Dropdown, Row } from "react-bootstrap";
import DayAvailabilities from "../day-availabilities/DayAvailabilities";
import TimeslotButton from "../timeslot-button/TimeslotButton";

import { usePhonePortraitContext } from "contexts/phone-portrait-context";

import { AmplitudePageName, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import AppointmentTypesSelect, {
  AppointmentTypesSelectOption,
} from "../appointment-types-select/AppointmentTypesSelect";
import styles from "./PatientBookCalendar.module.scss";
import PatientBookCalendarEmptyState from "./PatientBookCalendarEmptyState";

export const formatDateTime = (dateTime) => {
  // ex. Feb 11
  return new Date(dateTime).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const AMPLITUDE_COMPONENT_NAME = "patient-book-calendar";

interface Props {
  showThreeRows?: boolean;
  isClientPortal?: boolean;
  orchidPro: OrchidPro;
  schedule: Schedule;
  appointmentType: AppointmentTypesSelectOption;
  busyTimes: BlockedInterval[];
  openTimeslotModal?: () => void;
  openRequestDifferentTimeModal?: () => void;
  setSelectedTimeslot?: ((arg: any) => void) | undefined;
  amplitudePageName?: AmplitudePageName;
  isInModal?: boolean;
  appointmentTypes: SessionType[];
  selectedType?: AppointmentTypesSelectOption;
  setSelectedType: React.Dispatch<
    React.SetStateAction<AppointmentTypesSelectOption>
  >;
}

const PatientBookCalendar: React.FC<Props> = ({
  showThreeRows,
  isClientPortal,
  orchidPro,
  schedule,
  appointmentType,
  busyTimes,
  openTimeslotModal,
  openRequestDifferentTimeModal,
  setSelectedTimeslot,
  amplitudePageName,
  isInModal,
  appointmentTypes,
  selectedType,
  setSelectedType,
}) => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState("");
  const [showAllTimeslotsOfDay, setShowAllTimeslotsOfDay] = useState(false);

  const { isPhonePortrait } = usePhonePortraitContext();

  const changeWeek = useCallback(async (offset) => {
    setWeekOffset((prev) => Math.max(0, prev + offset));
  }, []);

  const getRelevantFreeSlots = useCallback(
    (date) =>
      getStillAvailableSlotsForDay(schedule, busyTimes, date, appointmentType),
    [appointmentType, busyTimes, schedule],
  );

  const _renderAppointmentsInCard = useCallback(
    (date) => {
      if (!appointmentType) return null;
      const relevantFreeSlots = getRelevantFreeSlots(date);
      return (
        <>
          {relevantFreeSlots.length === 0 && (
            <div className="rg-14 my-2 gray01">No availablity</div>
          )}
          {relevantFreeSlots.length >= 1 &&
            relevantFreeSlots.map((slot, index) => {
              return (
                slot.free &&
                (showAllTimeslotsOfDay ? (
                  <TimeslotButton
                    appointmentType={appointmentType}
                    orchidPro={orchidPro}
                    slot={slot}
                    key={index}
                    isClientPortal={isClientPortal}
                    setSelectedTimeslot={setSelectedTimeslot}
                  />
                ) : showThreeRows ? (
                  index <= 2 && (
                    <TimeslotButton
                      key={index}
                      appointmentType={appointmentType}
                      orchidPro={orchidPro}
                      slot={slot}
                      isClientPortal={isClientPortal}
                      setSelectedTimeslot={setSelectedTimeslot}
                    />
                  )
                ) : (
                  index <= 1 && (
                    <TimeslotButton
                      appointmentType={appointmentType}
                      orchidPro={orchidPro}
                      slot={slot}
                      key={index}
                    />
                  )
                ))
              );
            })}
        </>
      );
    },
    [
      getRelevantFreeSlots,
      appointmentType,
      isClientPortal,
      orchidPro,
      setSelectedTimeslot,
      showThreeRows,
      showAllTimeslotsOfDay,
    ],
  );

  const _renderAppointmentsInModal = useCallback(
    (date) => {
      if (!appointmentType) return null;
      const relevantFreeSlots = getRelevantFreeSlots(date);
      return relevantFreeSlots.length === 0 ? (
        <div className="gray01 rg-14">No available times</div>
      ) : (
        relevantFreeSlots.map((slot, index) => {
          return (
            slot.free && (
              <TimeslotButton
                appointmentType={appointmentType}
                orchidPro={orchidPro}
                slot={slot}
                isInModal={true}
                key={index}
              />
            )
          );
        })
      );
    },
    [getRelevantFreeSlots, appointmentType, orchidPro],
  );

  const checkNextThreeDaysFreeSlots = useMemo(() => {
    if (!appointmentType) {
      return null;
    }

    return [0, 1, 2].some((index) => {
      const date = new Date(today().getTime());
      date.setDate(date.getDate() + index + weekOffset * 3);

      return getRelevantFreeSlots(date).length > 0;
    });
  }, [getRelevantFreeSlots, appointmentType, weekOffset]);

  const findNextFreeSlot = useCallback(() => {
    if (checkNextThreeDaysFreeSlots === false) {
      // only find free slots in the next 49 days
      for (let i = 0; i < 49; ++i) {
        const date = new Date(today().getTime());
        date.setDate(date.getDate() + i + weekOffset * 3);
        const relevantFreeSlots = getRelevantFreeSlots(date);
        if (relevantFreeSlots.length !== 0) {
          return formatDateTime(relevantFreeSlots[0].start);
        } else {
          continue;
        }
      }
    } else {
      return null;
    }
  }, [checkNextThreeDaysFreeSlots, getRelevantFreeSlots, weekOffset]);

  // for client portal
  const checkIfMoreAvailabilities = useCallback(() => {
    let moreAvailabilities = false;
    [0, 1, 2].forEach((index) => {
      const date = new Date(today().getTime());
      date.setDate(date.getDate() + index + weekOffset * 3);
      const relevantFreeSlots = getRelevantFreeSlots(date);
      if (relevantFreeSlots.length > 3) {
        moreAvailabilities = true;
      }
    });
    return moreAvailabilities;
  }, [getRelevantFreeSlots, weekOffset]);

  const dateRangeHeader = useMemo(() => {
    const date = new Date(today().getTime());
    date.setDate(date.getDate() + 0 + weekOffset * 6);
    return (
      <div className="md-16 mb-0">
        {`${formatDateTime(date)} - ${formatDateTime(
          date.setDate(date.getDate() + 5),
        )}`}
      </div>
    );
  }, [weekOffset]);

  const nextFreeSlot = useMemo(
    () => (findNextFreeSlot() ? <> - {findNextFreeSlot()}</> : null),
    [findNextFreeSlot],
  );
  const selectedDay = useMemo(
    () =>
      selectedDate
        ? time_utils.jsday_to_daystr(new Date(selectedDate).getDay())
        : null,
    [selectedDate],
  );

  const trackClick = useCallback(
    (elementName: string) => {
      if (!amplitudePageName) {
        return;
      }
      track(
        amplitudePageName,
        [AMPLITUDE_COMPONENT_NAME, elementName],
        "click",
      );
    },
    [amplitudePageName],
  );

  const areAnySessionsAvailable = useMemo(
    () =>
      appointmentType && (checkNextThreeDaysFreeSlots === true || nextFreeSlot),
    [appointmentType, checkNextThreeDaysFreeSlots, nextFreeSlot],
  );

  if (isInModal) {
    return (
      <>
        <Row className={getClasses(!isPhonePortrait ? "px-4" : "p-0")}>
          <Col md={6} className="pl-0">
            <AppointmentTypesSelect
              appointmentTypes={appointmentTypes}
              orchidPro={orchidPro}
              selectedType={selectedType}
              amplitudeComponentName={AMPLITUDE_COMPONENT_NAME}
              amplitudePageName={amplitudePageName}
              setSelectedType={setSelectedType}
            />
          </Col>
          {!isPhonePortrait && (
            <Col md={6} className="pr-0">
              <div className="rg-12 gray01 mb-1">Select a day</div>
              <DropdownToggle
                title={selectedDate?.substring(0, 10) || "Pick a day"}
                Caret={<OutlineCalendar />}
                onClick={() => trackClick("select-a-day-dropdown-toggle")}
              >
                <Dropdown.Item onClick={() => setSelectedDate("")}>
                  Pick a day
                </Dropdown.Item>
                {[...Array(6).keys()].map((index) => {
                  const date = new Date(today().getTime());
                  date.setDate(date.getDate() + index + weekOffset * 6);
                  const day = time_utils.jsday_to_daystr(date.getDay());
                  return (
                    <Dropdown.Item
                      key={index}
                      onClick={() => setSelectedDate(date.toString())}
                    >
                      {`${day} ${formatDateTime(date)}`}
                    </Dropdown.Item>
                  );
                })}
              </DropdownToggle>
            </Col>
          )}
        </Row>

        <div
          className={
            !isPhonePortrait
              ? getClasses("px-4", styles["availability-timeslots__body"])
              : ""
          }
        >
          <Row className="align-items-center mb-3 justify-content-between">
            <Col md={6} className="p-0 d-inline-flex align-items-center">
              <Button
                variant="link"
                className="pl-0 darker"
                disabled={weekOffset <= 0}
                onClick={() => {
                  trackClick("previous-days-button");
                  changeWeek(-1);
                }}
              >
                <Previous />
              </Button>
              {dateRangeHeader}
              <Button
                variant="link"
                className="darker"
                onClick={() => {
                  trackClick("next-days-button");
                  changeWeek(1);
                }}
              >
                <Next />
              </Button>
            </Col>
          </Row>
          {selectedDate && selectedDay ? (
            <DayAvailabilities
              day={selectedDay}
              date={new Date(selectedDate)}
              appointments={_renderAppointmentsInModal(new Date(selectedDate))}
            />
          ) : (
            [...Array(6).keys()].map((index) => {
              const date = new Date(today().getTime());
              date.setDate(date.getDate() + index + weekOffset * 6);
              const day = time_utils.jsday_to_daystr(date.getDay());
              return (
                <DayAvailabilities
                  key={day}
                  day={day}
                  date={date}
                  appointments={_renderAppointmentsInModal(date)}
                />
              );
            })
          )}
        </div>
      </>
    );
  }

  if (!areAnySessionsAvailable) {
    return (
      <PatientBookCalendarEmptyState
        showThreeRows={showThreeRows}
        trackClick={trackClick}
        changeWeek={changeWeek}
        openRequestDifferentTimeModal={openRequestDifferentTimeModal}
        selectedType={selectedType}
      />
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <p className="md-14 mb-0">Select an available time</p>
        {!isClientPortal && (
          <Button
            variant="link"
            size="sm"
            className={"p-0"}
            onClick={() => {
              amplitudePageName && trackClick("request-different-time-button");
              openRequestDifferentTimeModal && openRequestDifferentTimeModal();
            }}
          >
            Request different time
          </Button>
        )}
      </div>
      <Row>
        <Col className={`p-0 ${styles["col-left"]}`}>
          <IconButton
            disabled={weekOffset <= 0}
            onClick={() => {
              trackClick("previous-days-button");
              if (isClientPortal) {
                setShowAllTimeslotsOfDay(false);
              }
              changeWeek(-1);
            }}
          >
            <Previous />
          </IconButton>
        </Col>
        {[...Array(3).keys()].map((index) => {
          const date = new Date(today().getTime());
          date.setDate(date.getDate() + index + weekOffset * 3);
          const day = time_utils.jsday_to_daystr(date.getDay());

          return (
            <Col className={`p-0 text-center ${styles["col-date"]}`} key={day}>
              <div className="rg-11 gray01">{day}</div>
              <div className="md-14">{formatDateTime(date)}</div>
              {_renderAppointmentsInCard(date)}
            </Col>
          );
        })}
        <Col className={`p-0 ${styles["col-right"]}`}>
          <IconButton
            onClick={() => {
              trackClick("next-days-button");
              if (isClientPortal) {
                setShowAllTimeslotsOfDay(false);
              }
              changeWeek(1);
            }}
          >
            <Next />
          </IconButton>
        </Col>
      </Row>
      {checkNextThreeDaysFreeSlots === false &&
        nextFreeSlot &&
        (isClientPortal ? null : (
          <div className="d-flex justify-content-center">
            <Button
              variant="link"
              className="rg-14"
              onClick={() => {
                trackClick("see-next-availability-button");
                openTimeslotModal && openTimeslotModal();
              }}
            >
              See next availability{nextFreeSlot}
            </Button>
          </div>
        ))}

      {isClientPortal
        ? !checkIfMoreAvailabilities()
          ? null
          : !showAllTimeslotsOfDay && (
              <div className="d-flex justify-content-center mt-3">
                <Button
                  size="sm"
                  variant="link"
                  className="p-0"
                  onClick={() => {
                    trackClick("view-all-availability-button");
                    setShowAllTimeslotsOfDay((prev) => !prev);
                  }}
                >
                  View all availability
                </Button>
              </div>
            )
        : checkNextThreeDaysFreeSlots !== false && (
            <div className="d-flex justify-content-center">
              <Button
                size="sm"
                variant="link"
                className="p-0"
                onClick={() => {
                  trackClick("view-all-availability-button");
                  openTimeslotModal && openTimeslotModal();
                }}
              >
                View all availability
              </Button>
            </div>
          )}
    </>
  );
};

export default PatientBookCalendar;
